import { c as _c } from "react/compiler-runtime";
import type { IdentifyRequirementLogin } from '@onefootprint/request-types';
import { Divider, LinkButton } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import { getLogger } from '@/idv/utils';
import type { Context } from '../../identify.types';
const {
  logInfo
} = getLogger();
type DifferentAccountOptionProps = {
  onLoginWithDifferentAccount?: () => void;
  context: {
    initArgs: Pick<Context['initArgs'], 'isComponentsSdk' | 'config'>;
    state: Pick<Context['state'], 'email' | 'phoneNumber'>;
  };
  requirement?: IdentifyRequirementLogin;
};

/**
 * If data was bootstrapped, show the option to provide different contact info directly to us
 * in case the bootstrap data is incorrect.
 *
 * NOTE: this is only used in very old SDKs that accept bootstrap data directly. Newer clients pass bootstrap data in the onboarding session.
 */
const DifferentAccountOption = t0 => {
  const $ = _c(16);
  const {
    onLoginWithDifferentAccount,
    context,
    requirement
  } = t0;
  const {
    initArgs: t1,
    state: t2
  } = context;
  const {
    isComponentsSdk,
    config
  } = t1;
  const {
    email,
    phoneNumber
  } = t2;
  const hasBootstrapData = email?.isBootstrap || phoneNumber?.isBootstrap || false;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "log-in-different-account"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("identify", t3);
  const playbookHidesOption = config?.flags.shouldHideBootstrappedLoginWithDifferent;
  const isStaticMatch = requirement?.user.isStaticMatch;
  const showLoginWithDifferentOption = hasBootstrapData && !playbookHidesOption && !isStaticMatch && !isComponentsSdk && !!onLoginWithDifferentAccount;
  if (!showLoginWithDifferentOption) {
    return null;
  }
  let t4;
  if ($[1] !== onLoginWithDifferentAccount) {
    t4 = () => {
      logInfo("Login with different account");
      onLoginWithDifferentAccount();
    };
    $[1] = onLoginWithDifferentAccount;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const handleLoginWithDifferent = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Divider variant="secondary" />;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t) {
    t6 = t(_temp);
    $[4] = t;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t6) {
    t7 = <div className="relative isolate w-full min-h-6 flex justify-center items-center">{t5}<div className="text-sm text-quaternary bg-primary p-2 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-2">{t6}</div></div>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t) {
    t8 = t(_temp2);
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== handleLoginWithDifferent || $[11] !== t8) {
    t9 = <div className="flex flex-row justify-center w-full"><LinkButton onClick={handleLoginWithDifferent} data-dd-action-name="different-account:cta">{t8}</LinkButton></div>;
    $[10] = handleLoginWithDifferent;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t7 || $[14] !== t9) {
    t10 = <div className="flex flex-col gap-6">{t7}{t9}</div>;
    $[13] = t7;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};
export default DifferentAccountOption;
function _temp($) {
  return $.or;
}
function _temp2($_0) {
  return $_0.label;
}